import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, ChevronDown, ChevronRight, Package, Scale, Users,
    Check, Phone, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewPackageSoon — session-count alert buckets (Sachin 2026-08).
 * Families grouped by sessions remaining (5→1, then 0 split members /
 * non-members) + an Open IOUs bucket. Each bucket is a collapsible row;
 * each family links to its profile for a two-click new-pack charge.
 */

const BUCKET_STYLE = {
    "5": { chip: "border-zinc-600 text-zinc-300" },
    "4": { chip: "border-zinc-600 text-zinc-300" },
    "3": { chip: "border-amber-600 text-amber-300" },
    "2": { chip: "border-amber-500 text-amber-200" },
    "1": { chip: "border-rose-600 text-rose-300" },
    "0_non_members": { chip: "border-rose-500 text-rose-200" },
    "0_members": { chip: "border-emerald-700 text-emerald-300" },
    "ious": { chip: "border-rose-500 text-rose-200" },
};

function FamilyRow({ f, bucketKey }) {
    return (
        <Link
            to={`/coach/leads/${f.user_id}`}
            data-testid={`nps-family-${bucketKey}-${f.user_id}`}
            className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-zinc-900/60 transition-colors group"
        >
            <div className="min-w-0">
                <div className="text-sm text-white truncate group-hover:text-brand-red transition-colors">
                    {f.family_name}
                    {f.is_member && (
                        <span className="ml-2 text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 rounded border border-emerald-800 text-emerald-400">
                            Member
                        </span>
                    )}
                </div>
                {f.athletes?.length > 0 && (
                    <div className="text-[11px] text-zinc-500 truncate mt-0.5 flex items-center gap-1">
                        <Users className="w-3 h-3 flex-shrink-0" />
                        {f.athletes.join(" · ")}
                    </div>
                )}
                <div className="text-[10px] text-zinc-600 mt-0.5 flex items-center gap-3">
                    {f.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{f.email}</span>}
                    {f.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{f.phone}</span>}
                </div>
            </div>
            <div className="text-right flex-shrink-0">
                {bucketKey === "ious" ? (
                    <span className="text-[10px] tracking-[0.15em] uppercase text-rose-300 font-semibold">
                        {f.iou_count} IOU{f.iou_count === 1 ? "" : "s"}
                    </span>
                ) : (
                    <span className="text-[10px] tracking-[0.15em] uppercase text-zinc-400">
                        {f.remaining} left
                    </span>
                )}
            </div>
        </Link>
    );
}

function Bucket({ b, open, onToggle }) {
    const style = BUCKET_STYLE[b.key] || BUCKET_STYLE["5"];
    const Icon = b.key === "ious" ? Scale : Package;
    return (
        <div className="border border-zinc-800 rounded-lg overflow-hidden bg-zinc-950/50">
            <button
                type="button"
                data-testid={`nps-bucket-${b.key}`}
                onClick={onToggle}
                disabled={b.count === 0}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors ${
                    b.count === 0 ? "opacity-50 cursor-default" : "hover:bg-zinc-900/60"
                }`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
                    <Icon className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                    <span className="text-sm text-white">{b.label}</span>
                </div>
                <span
                    data-testid={`nps-bucket-count-${b.key}`}
                    className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-full border font-semibold ${style.chip}`}
                >
                    {b.count === 0 ? "None" : `${b.count} ${b.count === 1 ? "family" : "families"}`}
                </span>
            </button>
            {open && b.count > 0 && (
                <div className="border-t border-zinc-900 divide-y divide-zinc-900">
                    {b.families.map((f) => (
                        <FamilyRow key={f.user_id} f={f} bucketKey={b.key} />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function NewPackageSoon() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState({});

    useEffect(() => {
        axios.get(`${API}/coach/new-package-soon`, { withCredentials: true })
            .then((r) => setData(r.data))
            .catch((e) => {
                toast.error("Couldn't load session counts", {
                    description: e?.response?.data?.detail || "Try again in a moment.",
                });
                setData({ buckets: [], alert_count: 0 });
            });
    }, []);

    if (data === null) {
        return (
            <div className="flex items-center justify-center p-8 text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading session counts…
            </div>
        );
    }

    const total = data.alert_count || 0;

    return (
        <div data-testid="new-package-soon-page" className="max-w-3xl mx-auto p-6">
            <div className="mb-6">
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">Session alerts</div>
                <h1 className="font-display text-2xl tracking-wider">New Package Soon</h1>
                <p className="text-xs text-zinc-400 mt-1 max-w-xl">
                    Families grouped by pack sessions remaining — tap a group to see who's
                    running low, tap a family to open their profile and charge a new pack.
                    Session pools are shared per family.
                </p>
            </div>

            {total === 0 && (
                <div
                    data-testid="nps-all-clear"
                    className="border border-emerald-800/50 bg-emerald-950/20 rounded-xl p-5 mb-5 text-emerald-300 text-sm flex items-center gap-2"
                >
                    <Check className="w-4 h-4" /> Nobody is running low right now.
                </div>
            )}

            <div className="space-y-2.5">
                {(data.buckets || []).map((b) => (
                    <Bucket
                        key={b.key}
                        b={b}
                        open={!!open[b.key]}
                        onToggle={() => setOpen((p) => ({ ...p, [b.key]: !p[b.key] }))}
                    />
                ))}
            </div>
        </div>
    );
}
